import {
    type NextLocale,
    type PreferencesData,
    type UserData,
    GENRES,
    PREFERENCES,
    TARGETS,
    VENUE_IDS,
} from '@beat-venues/common-types';
import {
    Button,
    CheckboxSlider,
    CheckboxGroup,
    Container,
    Heading,
    Paragraph,
} from '@beat-venues/ui/react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { ChangeEvent, FormEvent, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Form, useSearchParams } from 'react-router-dom';

import {
    getRecaptchaToken,
    isValidEmail,
    sanitizeQueryParams,
} from '../../utils';
import { BirthdayPicker } from './birthday-picker';
import { NewUserForm } from './new-user-form';
import { SuccessPreferences } from './success';
import { usePreferencesStore } from '../../states/preferences-state';

interface PreferencesProps {
    user?: UserData;
    isUserPending?: boolean;
    isAuthenticated?: boolean;
    userId?: string;
}

export const LayoutPreferences = ({
    user,
    isUserPending,
    isAuthenticated,
    userId,
}: PreferencesProps) => {
    const [searchParams] = useSearchParams();
    const safeParams = sanitizeQueryParams(searchParams);
    const userLanguage = safeParams.language as NextLocale;
    const visitorHash = safeParams.hash;
    const rawVisitorEmail = safeParams.email;
    const visitorEmail =
        rawVisitorEmail && rawVisitorEmail.includes(' ')
            ? rawVisitorEmail.replace(/ /g, '+')
            : rawVisitorEmail;
    const crew = safeParams.show;
    const sourceOptIns = safeParams.preferences?.split(',');
    const [isAllOptOut, setIsAllOptOut] = useState(false);
    const { t, i18n } = useTranslation('');
    const isValidPreference = (
        preference: string
    ): preference is PreferencesData => {
        return Object.values(PREFERENCES).includes(
            preference as PreferencesData
        );
    };

    const { preferences, setPreferences } = usePreferencesStore();

    const composePreferences = (): PreferencesData[] => {
        if (sourceOptIns && sourceOptIns.length > 0) {
            const validPreferences = sourceOptIns.filter((sourceOptIn) =>
                isValidPreference(sourceOptIn)
            );
            return [...preferences, ...validPreferences];
        }
        return preferences;
    };
    const [activePref, setActivePref] = useState<PreferencesData[]>(
        composePreferences()
    );
    const [optOuts, setOptOuts] = useState<PreferencesData[]>([]);
    const [newUser, setNewUser] = useState<
        Pick<UserData, 'email' | 'firstName' | 'lastName' | 'language'>
    >({
        email: isValidEmail(visitorEmail) ? visitorEmail : user?.email ?? '',
        language: userLanguage ?? i18n.language,
    });
    const [isSubmitDisabled, setIsSubmitDisabled] = useState(false);
    const [errorMessage, setErrorMessage] = useState<string | null>(null);
    const [successMessage, setSuccessMessage] = useState<string | null>(null);
    const [birthdate, setBirthdate] = useState<string>(user?.birthdate || '');
    const [isUser, setIsUser] = useState<boolean>(false);

    const filteredVenues = [
        'beat-tickets',
        'channel-business',
        'beat',
        'channel-corporate',
        'sportpaleis',
    ];

    const mutation = useMutation({
        mutationFn: async ({
            preference,
            isChecked,
            recaptchaToken,
        }: {
            preference: PreferencesData[];
            isChecked: boolean;
            recaptchaToken: string;
        }) => {
            const response = await fetch(
                `/api/preferences/${isChecked ? 'optIn' : 'optOut'}`,
                {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                    },
                    body: JSON.stringify({
                        email: newUser.email || user?.email,
                        preferences: preference,
                        firstName: newUser?.firstName ?? user?.firstName,
                        lastName: newUser?.lastName ?? user?.lastName,
                        language: user?.language ?? newUser.language,
                        ...(birthdate && { birthdate: birthdate }),
                        recaptchaToken,
                        ...(userId && { id: userId }),
                    }),
                }
            );

            if (!response.ok) {
                throw new Error(t('error.preferences') || '');
            }
            setPreferences(
                isChecked
                    ? [...preference]
                    : preferences.filter((pref) => !optOuts.includes(pref))
            );

            return response.json();
        },
        onError: (error) => {
            setErrorMessage(error.message);
        },
    });

    // automated submit call on load if there are valid email and preferences
    const { isLoading } = useQuery({
        queryKey: ['automatic-submit'],
        queryFn: async () => {
            // Only run this query on the first visit
            const referrer = (
                performance.getEntriesByType(
                    'navigation'
                )[0] as PerformanceNavigationTiming
            ).type;
            if (
                sourceOptIns &&
                sourceOptIns.length > 0 &&
                visitorEmail &&
                isValidEmail(visitorEmail) &&
                referrer === 'navigate'
            ) {
                const validOptIns = sourceOptIns.filter((sourceOptIn) =>
                    isValidPreference(sourceOptIn)
                );
                await mutation.mutateAsync({
                    preference: [...validOptIns],
                    isChecked: true,
                    recaptchaToken: await getRecaptchaToken(),
                });
            }
            return '200 OK';
        },
    });

    const handlePreferences = async (
        e: ChangeEvent<HTMLInputElement>,
        preferences: PreferencesData
    ) => {
        const isChecked = e.target.checked;
        setActivePref((prevPref) =>
            isChecked
                ? [...prevPref, preferences]
                : prevPref.filter((pref) => pref !== preferences)
        );
        setOptOuts((prevOptOut) =>
            !isChecked
                ? [...prevOptOut, preferences]
                : prevOptOut.filter((pref) => pref !== preferences)
        );
    };

    const handleSubmit = async (e: FormEvent) => {
        setErrorMessage(null);
        setSuccessMessage(null);
        setIsSubmitDisabled(true);
        e.preventDefault();

        const validOptOuts = optOuts.filter((optOut) =>
            preferences.includes(optOut)
        );

        const isDifferent =
            JSON.stringify(activePref) !== JSON.stringify(preferences) &&
            !activePref.every((item) => preferences.includes(item));

        try {
            if (validOptOuts.length > 0) {
                await mutation.mutateAsync({
                    preference: validOptOuts,
                    isChecked: false,
                    recaptchaToken: await getRecaptchaToken(),
                });
            }

            setIsAllOptOut(
                preferences.length === validOptOuts.length &&
                    activePref.length === 0
            );

            if (activePref.length > 0 && isDifferent) {
                await mutation.mutateAsync({
                    preference: activePref,
                    isChecked: true,
                    recaptchaToken: await getRecaptchaToken(),
                });
            }

            setSuccessMessage(t('messages.successUpdate'));
        } catch (error) {
            console.error(
                `[LayoutPreferences - handleSubmit] Error submitting preferences: ${error}`
            );
            setErrorMessage(t('error.preferences'));
        } finally {
            setIsSubmitDisabled(false);
            setOptOuts([]);
        }
    };

    // Mark given user preferences
    useEffect(() => {
        setActivePref(() => composePreferences());
    }, [newUser.email, preferences]);

    // get birthdate from user preferences
    const { status } = useQuery({
        queryKey: [
            'get-birthdate',
            user,
            activePref?.includes('NB_happy_birthday'),
        ],
        queryFn: async (): Promise<string | undefined> => {
            try {
                if (activePref?.includes('NB_happy_birthday')) {
                    if (!birthdate && user?.birthdate) {
                        setBirthdate(user.birthdate);
                    } else if (isValidEmail(visitorEmail) && visitorHash) {
                        const result = await fetch(
                            `/api/sfUser/getSFUser?emails=${visitorEmail}&id=${visitorHash}`
                        );
                        if (result.ok) {
                            const response = await result.json();
                            setBirthdate(response.birthdate || '');
                            if (response.birthdate) {
                                setIsUser(true);
                            }
                        }
                    }
                }
            } catch (error) {
                console.error('Error fetching birthdate:', error);
                return 'failed';
            }
            return status;
        },
        enabled: !birthdate, // Only fetch if birthdate is not already set
    });

    return (
        <Container className="mb-40">
            {(successMessage || user) && (
                <Heading as="h1" className="font-light xl:ml-0" size="h1">
                    {t('navigation.preferences')}
                </Heading>
            )}
            {errorMessage && (
                <Paragraph className="bg-danger-500 my-4 rounded-sm p-2 pl-4">
                    {errorMessage}
                </Paragraph>
            )}
            {successMessage && user && (
                <Paragraph className="bg-success-500 my-4 rounded-sm p-2 pl-5">
                    &#10003; {t('messages.successUpdate')}
                </Paragraph>
            )}
            {successMessage && !user ? (
                <SuccessPreferences isAllOptOut={isAllOptOut} />
            ) : (
                <Form onSubmit={handleSubmit}>
                    {!isAuthenticated && (
                        <NewUserForm
                            newUser={newUser}
                            setNewUser={setNewUser}
                            userLanguage={userLanguage}
                        />
                    )}
                    <div
                        className={`${
                            isUserPending ? 'blur-md' : undefined
                        } mb-4 block gap-4 lg:flex`}
                    >
                        <CheckboxGroup title={t('preferences.newsLetters')}>
                            {VENUE_IDS.filter(
                                (venue) => !filteredVenues?.includes(venue)
                            ).map((venueId, index) => (
                                <div
                                    key={venueId}
                                    className="relative overflow-hidden"
                                >
                                    <CheckboxSlider
                                        checked={activePref?.includes(
                                            PREFERENCES[
                                                venueId as keyof typeof PREFERENCES
                                            ]
                                        )}
                                        label={t(`venues.${venueId}.name`)}
                                        venueId={venueId}
                                        onChange={(e) => {
                                            handlePreferences(
                                                e,
                                                PREFERENCES[
                                                    venueId as keyof typeof PREFERENCES
                                                ]
                                            );
                                        }}
                                    />
                                    {index <
                                        VENUE_IDS.length -
                                            filteredVenues.length -
                                            1 && (
                                        <hr className="mt-3 border-gray-100" />
                                    )}
                                </div>
                            ))}
                        </CheckboxGroup>
                        <div className="w-full">
                            <CheckboxGroup
                                title={t('preferences.genres.genres')}
                            >
                                {GENRES.map((genre, index) => (
                                    <div key={genre}>
                                        <CheckboxSlider
                                            checked={activePref?.includes(
                                                PREFERENCES[genre]
                                            )}
                                            genre={genre}
                                            label={t(
                                                `preferences.genres.${genre}`
                                            )}
                                            onChange={(e) => {
                                                handlePreferences(
                                                    e,
                                                    PREFERENCES[genre]
                                                );
                                            }}
                                        />
                                        {index < GENRES.length - 1 && (
                                            <hr className="mt-3 border-gray-100" />
                                        )}
                                    </div>
                                ))}
                            </CheckboxGroup>
                            <CheckboxGroup title={t('preferences.other.other')}>
                                {TARGETS.filter((target) =>
                                    !crew ? target !== 'crew' : TARGETS
                                ).map((target, index) => (
                                    <div key={target}>
                                        <CheckboxSlider
                                            className="my-1 -ml-2"
                                            checked={activePref?.includes(
                                                PREFERENCES[target]
                                            )}
                                            label={t(
                                                `preferences.other.${target}`
                                            )}
                                            venueId="beat"
                                            onChange={(e) => {
                                                handlePreferences(
                                                    e,
                                                    PREFERENCES[target]
                                                );
                                            }}
                                        />
                                        {index <
                                            TARGETS.length - (crew ? 1 : 2) && (
                                            <hr className="border-gray-100" />
                                        )}
                                        {activePref?.includes(
                                            'NB_happy_birthday'
                                        ) &&
                                            target === 'birthdate' && (
                                                <BirthdayPicker
                                                    birthday={birthdate}
                                                    setBirthday={setBirthdate}
                                                    isUser={
                                                        user !== undefined ||
                                                        isUser
                                                    }
                                                />
                                            )}
                                    </div>
                                ))}
                            </CheckboxGroup>
                        </div>
                    </div>
                    <Button
                        className={`${isUserPending ? 'blur-md' : undefined}`}
                        disabled={
                            mutation.isPending || isSubmitDisabled || isLoading
                        }
                        intent="primary"
                        type="submit"
                    >
                        {t('userAccount.submit')}
                    </Button>
                </Form>
            )}
        </Container>
    );
};
